// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type {
  CsvImportEnvelope,
  ValidationErrorField,
} from '@/csv-import/schemas';
import type {
  CsvImportFailedRow,
  CsvImportResultBucket,
  CsvImportResultsView,
} from '@/csv-import/types';
import { pluralize } from '@/utilities/pluralize';
import { createIdentifierSuffix } from '@/utilities/upload-helpers';
import { Typography } from '@mui/material';

/**
 * One row of an update uploader's response. Every update endpoint returns this shape,
 * keyed by the record's Hardfin ID, so the results view is built the same way for all
 * of them.
 */
interface CsvImportUpdateItem {
  index: number;
  key: string;
  updated: boolean;
  noOp: boolean;
  fieldsUpdated: Record<string, boolean>;
  errors: ValidationErrorField[];
}

interface CsvImportUpdateEnvelope extends CsvImportEnvelope {
  items: CsvImportUpdateItem[];
}

interface UpdateResultsViewOptions {
  /** Header of the identifier column, named in each failed row so the user can find it */
  keyColumnHeader: string;
  /** Noun for the records being updated, used for bucket titles and counts */
  recordNoun: { singular: string; plural: string };
  /** Name of the upload in the success message, such as "cost data" */
  dataLabel: string;
}

function formatErrors(errors: ValidationErrorField[]): string[] {
  return errors.map(
    (err) => `Column "${err.fieldName}" has an error: ${err.error}`
  );
}

function buildBucketRows(items: CsvImportUpdateItem[]) {
  return items.map((item) => ({
    key: `${item.index}-${item.key}`,
    content: (
      <Typography variant="body2">
        Row {item.index} — {item.key}
      </Typography>
    ),
  }));
}

function sentenceCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Builds the results view for an update uploader: rows that were written, rows that
 * matched what was already stored, and rows that failed with their errors.
 */
export function buildUpdateResultsView(
  result: CsvImportUpdateEnvelope,
  options: UpdateResultsViewOptions
): CsvImportResultsView {
  const failedRows: CsvImportFailedRow[] = [];

  result.rowErrors.forEach((row) => {
    if (row.errors.length > 0) {
      failedRows.push({
        rowNumber: row.index,
        primaryText: `For file row ${row.index}:`,
        errors: formatErrors(row.errors),
      });
    }
  });

  const validItems: CsvImportUpdateItem[] = [];
  result.items.forEach((item) => {
    if (item.errors.length > 0) {
      failedRows.push({
        rowNumber: item.index,
        identifierSuffix: createIdentifierSuffix(
          item,
          'key',
          options.keyColumnHeader
        ),
        primaryText: `For file row ${item.index}${item.key ? ` (${options.keyColumnHeader} "${item.key}")` : ''}:`,
        errors: formatErrors(item.errors),
      });
    } else {
      validItems.push(item);
    }
  });

  const buckets: CsvImportResultBucket[] = result.success
    ? [
        {
          key: 'updated',
          title: `${sentenceCase(options.recordNoun.plural)} updated`,
          tone: 'success',
          rows: buildBucketRows(validItems.filter((item) => item.updated)),
        },
        {
          key: 'noOp',
          title: `${sentenceCase(options.recordNoun.plural)} with no updates`,
          tone: 'neutral',
          rows: buildBucketRows(validItems.filter((item) => !item.updated)),
        },
      ]
    : [
        {
          key: 'valid',
          title: 'Valid rows not applied',
          tone: 'neutral',
          rows: buildBucketRows(validItems),
        },
      ];

  return {
    success: result.success,
    successMessage: `Your ${options.dataLabel} uploaded successfully for ${pluralize(
      result.items.length,
      options.recordNoun
    )}`,
    buckets,
    failedRows,
    failedRowNoun: options.recordNoun,
    structureErrors: result.structureErrors,
  };
}
